import {
    type FormEvent,
    type ReactNode,
    useEffect,
    useId,
    useState,
} from 'react'
import { HelpCircle, Mail, Send } from 'lucide-react'
import {
    type EmailSettings,
    getEmailSettings,
    sendTestEmail,
    updateEmailSettings,
} from '~/api/notifications'
import { PageHeader } from '~/components/PageHeader'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '~/components/ui/card'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Skeleton } from '~/components/ui/skeleton'
import { Spinner } from '~/components/ui/spinner'
import { Toggle } from '~/components/ui/toggle'
import { useToast } from '~/components/ui/toast'

// 알림·발송 설정: 시스템 SMTP 릴레이(인증·재설정·초대 메일 발송) 설정 + 테스트 발송.
// DB에 저장된 값이 환경변수보다 우선한다. 비밀번호는 write-only(비우면 기존 유지).

interface FormState {
    host: string
    port: string
    username: string
    fromAddress: string
    useTls: boolean
    password: string
}

function toForm(s: EmailSettings): FormState {
    return {
        host: s.host ?? '',
        port: String(s.port ?? 465),
        username: s.username ?? '',
        fromAddress: s.fromAddress ?? '',
        useTls: s.useTls,
        password: '', // 비밀번호는 내려오지 않으므로 항상 빈 값으로 시작(비우면 기존 유지)
    }
}

export function NotificationSettingsPage() {
    const toast = useToast()
    const [settings, setSettings] = useState<EmailSettings | null>(null)
    const [form, setForm] = useState<FormState | null>(null)
    const [saving, setSaving] = useState(false)
    const [recipient, setRecipient] = useState('')
    const [testing, setTesting] = useState(false)
    const [testResult, setTestResult] = useState<{
        ok: boolean
        detail: string
    } | null>(null)

    const load = () => {
        getEmailSettings()
            .then((s) => {
                setSettings(s)
                setForm(toForm(s))
            })
            .catch((e) => toast.error((e as Error).message))
    }

    useEffect(load, [])

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((f) => (f ? { ...f, [key]: value } : f))
    }

    const onSave = async (e: FormEvent) => {
        e.preventDefault()
        if (!form) return
        setSaving(true)
        try {
            const saved = await updateEmailSettings({
                host: form.host.trim(),
                port: Number(form.port) || 465,
                username: form.username.trim(),
                fromAddress: form.fromAddress.trim(),
                useTls: form.useTls,
                enabled: true,
                password:
                    form.password.trim() === '' ? null : form.password.trim(),
            })
            setSettings(saved)
            setForm(toForm(saved)) // 비밀번호 입력칸을 비운다(저장 후)
            toast.success('메일(SMTP) 설정을 저장했어요.')
        } catch (err) {
            toast.error((err as Error).message)
        } finally {
            setSaving(false)
        }
    }

    const onTest = async () => {
        if (recipient.trim() === '') return
        setTesting(true)
        setTestResult(null)
        try {
            const res = await sendTestEmail(recipient.trim())
            setTestResult(res)
            if (res.ok) {
                toast.success('테스트 메일을 보냈어요.')
            } else {
                toast.error(`테스트 발송 실패: ${res.detail}`)
            }
        } catch (err) {
            const message = (err as Error).message
            setTestResult({ ok: false, detail: message })
            toast.error(message)
        } finally {
            setTesting(false)
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow='플랫폼'
                title='알림·발송 설정'
                description='인증·재설정·초대 메일을 보내는 시스템 SMTP 릴레이를 설정하고 테스트 메일로 확인합니다.'
            />

            <Card className='max-w-2xl'>
                <CardHeader>
                    <div className='flex items-center justify-between gap-2'>
                        <div className='flex items-center gap-2'>
                            <Mail className='text-muted-foreground size-4' />
                            <CardTitle>메일(SMTP) 설정</CardTitle>
                        </div>
                        {settings ? (
                            <Badge
                                tone={
                                    settings.source === 'db'
                                        ? 'success'
                                        : 'neutral'
                                }>
                                {settings.source === 'db'
                                    ? 'DB 저장값'
                                    : '환경변수 폴백'}
                            </Badge>
                        ) : null}
                    </div>
                    <CardDescription>
                        인증·재설정·초대 메일을 보내는 SMTP 릴레이입니다. DB에
                        저장된 값이 환경변수보다 우선합니다. 변경 후엔 아래에서
                        테스트 메일로 확인하세요.
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    {!form ? (
                        <div className='space-y-4'>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} className='h-8 w-full' />
                            ))}
                        </div>
                    ) : (
                        <form onSubmit={onSave} className='space-y-4'>
                            <div className='grid grid-cols-[1fr_140px] gap-4'>
                                <Field
                                    label='호스트'
                                    help='SMTP 릴레이 서버 주소예요. 예: smtp.gmail.com'
                                    value={form.host}
                                    onChange={(v) => set('host', v)}
                                    placeholder='smtp.example.com'
                                    required
                                />
                                <Field
                                    label='포트'
                                    help='465는 SSL(암시적 TLS), 587은 STARTTLS를 씁니다.'
                                    value={form.port}
                                    onChange={(v) => set('port', v)}
                                    type='number'
                                    placeholder='465'
                                    required
                                />
                            </div>

                            <Field
                                label='사용자'
                                help='SMTP 인증 사용자명(보통 발신 계정 이메일)이에요.'
                                value={form.username}
                                onChange={(v) => set('username', v)}
                                placeholder='no-reply@pai-world.com'
                            />

                            <Field
                                label='비밀번호'
                                help='SMTP 인증 비밀번호예요. 암호화 저장되며 다시 표시되지 않아요. 비우면 기존 비밀번호를 유지합니다.'
                                value={form.password}
                                onChange={(v) => set('password', v)}
                                type='password'
                                placeholder={
                                    settings?.secretSet
                                        ? '저장됨 — 변경하려면 새 비밀번호 입력'
                                        : '비밀번호 입력'
                                }
                                hint={
                                    settings?.secretSet
                                        ? '저장됨(비우면 유지)'
                                        : undefined
                                }
                            />

                            <Field
                                label='발신 주소'
                                help='받는 사람에게 보이는 From 주소예요. 예: HACCP AI <no-reply@pai-world.com>'
                                value={form.fromAddress}
                                onChange={(v) => set('fromAddress', v)}
                                placeholder='HACCP AI <no-reply@pai-world.com>'
                            />

                            <div className='flex items-center gap-2'>
                                <Toggle
                                    checked={form.useTls}
                                    onCheckedChange={(v) => set('useTls', v)}
                                    label='STARTTLS 사용(587 등 비‑SSL 포트)'
                                />
                                <FieldHelp text='465(SSL)이 아닌 포트에서 STARTTLS로 암호화 연결을 맺을지 여부예요.' />
                            </div>

                            <div className='flex justify-end pt-1'>
                                <Button
                                    type='submit'
                                    size='sm'
                                    disabled={saving}>
                                    {saving ? <Spinner size='sm' /> : '저장'}
                                </Button>
                            </div>
                        </form>
                    )}

                    {/* 구분선: 테스트 메일 */}
                    <div className='border-border mt-6 border-t pt-5'>
                        <div className='mb-1 flex items-center gap-2'>
                            <h3 className='text-foreground text-[13px] font-semibold'>
                                테스트 메일
                            </h3>
                            <FieldHelp text='저장된(또는 환경변수) 설정으로 이 주소에 짧은 테스트 메일을 보냅니다.' />
                        </div>
                        <p className='text-muted-foreground mb-3 text-[12px]'>
                            설정이 올바른지 실제 발송으로 확인하세요.
                        </p>
                        <div className='flex items-start gap-2'>
                            <Input
                                type='email'
                                value={recipient}
                                onChange={(e) => setRecipient(e.target.value)}
                                placeholder='받는 주소 (you@example.com)'
                                className='max-w-xs'
                                aria-label='테스트 메일 받는 주소'
                            />
                            <Button
                                type='button'
                                variant='secondary'
                                size='sm'
                                onClick={onTest}
                                disabled={testing || recipient.trim() === ''}>
                                {testing ? (
                                    <Spinner size='sm' />
                                ) : (
                                    <>
                                        <Send />
                                        테스트 발송
                                    </>
                                )}
                            </Button>
                        </div>
                        {testResult ? (
                            <p
                                className={
                                    testResult.ok
                                        ? 'text-success mt-2 text-[12px]'
                                        : 'text-destructive mt-2 text-[12px]'
                                }
                                role='status'>
                                {testResult.ok
                                    ? '발송 성공 — '
                                    : '발송 실패 — '}
                                {testResult.detail}
                            </p>
                        ) : null}
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

// 작은 (?) 도움말 — 네이티브 title 툴팁 + 접근성 라벨. 색만으로 정보를 전달하지 않는다.
function FieldHelp({ text }: { text: string }) {
    return (
        <span
            role='img'
            aria-label={text}
            title={text}
            className='text-muted-foreground hover:text-foreground inline-flex cursor-help items-center'>
            <HelpCircle className='size-3.5' />
        </span>
    )
}

function Field({
    label,
    help,
    value,
    onChange,
    type = 'text',
    placeholder,
    required = false,
    hint,
}: {
    label: string
    help: string
    value: string
    onChange: (v: string) => void
    type?: string
    placeholder?: string
    required?: boolean
    hint?: ReactNode
}) {
    const id = useId()
    return (
        <div className='flex flex-col gap-1.5'>
            <div className='flex items-center gap-1.5'>
                <Label htmlFor={id}>{label}</Label>
                <FieldHelp text={help} />
                {hint ? (
                    <span className='text-muted-foreground text-[11px]'>
                        {hint}
                    </span>
                ) : null}
            </div>
            <Input
                id={id}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
            />
        </div>
    )
}
